import type { ChildTaxonDto } from '@/api/TaxonomyApi/generated/model/childTaxonDto';
import type { RankNameDto } from '@/api/TaxonomyApi/generated/model/rankNameDto';
import type { TaxonWithChildrenDto } from '@/api/TaxonomyApi/generated/model/taxonWithChildrenDto';
import type { ListSectionProps } from '@/components/List/list.types';
import { Ionicons } from '@expo/vector-icons';
import { SupportedLanguage } from '@store/i18n';
import {
  BuildTaxonomyItemsParams,
  LastAssignedItemResult,
  TaxonomyListItem,
} from './speciesTaxonomy.types';

export const normalizeTaxonomyLanguage = (
  language: string,
): 'fr' | 'es' | 'en' => {
  if (language.startsWith('fr')) {
    return 'fr';
  }
  if (language.startsWith('es')) {
    return 'es';
  }
  return 'en';
};

export const resolveRankLabel = (
  language: string,
  rankNames: RankNameDto[] | undefined,
): string | undefined => {
  if (!rankNames || rankNames.length === 0) {
    return undefined;
  }
  const normalizedLanguage = normalizeTaxonomyLanguage(language);
  return (
    rankNames.find((rankName) => rankName.language === normalizedLanguage)
      ?.name ??
    rankNames.find((rankName) => rankName.language === 'en')?.name ??
    rankNames.find((rankName) => rankName.language === 'fr')?.name ??
    rankNames.find((rankName) => rankName.language === 'es')?.name ??
    rankNames[0]?.name
  );
};

export const resolveVernacularLabel = (
  language: string,
  taxon: Pick<ChildTaxonDto, 'vernacularNames' | 'scientificName' | 'code'>,
): string => {
  const normalizedLanguage = normalizeTaxonomyLanguage(language);
  const localizedName = taxon.vernacularNames.find(
    (item: ChildTaxonDto['vernacularNames'][number]) =>
      item.language === normalizedLanguage,
  )?.name;
  return (
    localizedName ??
    taxon.vernacularNames[0]?.name ??
    taxon.scientificName ??
    taxon.code
  );
};

export const resolveScientificDisplayName = (
  taxon: Pick<ChildTaxonDto, 'genus' | 'scientificName'>,
): string => {
  return taxon.scientificName ?? '';
};

export const buildTaxonItem = (
  taxon: ChildTaxonDto,
  rankCode: string,
  language: SupportedLanguage,
): TaxonomyListItem => ({
  id: taxon.code,
  label: resolveVernacularLabel(language, taxon),
  scientificDisplayName: resolveScientificDisplayName(taxon),
  taxonCode: taxon.code,
  rankCode: rankCode,
});

export const buildTaxonomyItems = ({
  language,
  rank,
  hasNextRank,
  activeSelectionId,
  unassignedLabel,
}: BuildTaxonomyItemsParams): TaxonomyListItem[] => {
  if (!rank) {
    return [] as TaxonomyListItem[];
  }

  const items: TaxonomyListItem[] = [];

  if (hasNextRank) {
    items.push({
      id: `${activeSelectionId}-unassigned`,
      label: unassignedLabel,
      scientificDisplayName: resolveRankLabel(language, rank.names) ?? '',
      isUnassigned: true,
    });
  }

  items.push(
    ...rank.taxons.map((taxon) => buildTaxonItem(taxon, rank.code, language)),
  );

  return items;
};

type TaxonResponseSummary = Pick<
  TaxonWithChildrenDto,
  'code' | 'rank' | 'childrenByRank'
>;

export const hasTaxonChildren = (
  taxonResponse?: TaxonResponseSummary,
): boolean =>
  Boolean(taxonResponse?.childrenByRank.some((rank) => rank.taxons.length > 0));

export const isRootTaxonResponse = (
  taxonResponse: TaxonResponseSummary | undefined,
  rootTaxonCode: string,
): boolean => !taxonResponse || taxonResponse.code === rootTaxonCode;

export const isLeafSelection = (
  selection: TaxonomyListItem | undefined,
  taxonResponse: TaxonResponseSummary | undefined,
): boolean =>
  Boolean(
    selection?.taxonCode &&
    !selection.isUnassigned &&
    selection.id === taxonResponse?.code &&
    !hasTaxonChildren(taxonResponse),
  );

export const isUnsupportedLeafTaxonomySelection = ({
  selection,
  taxonResponse,
  rootTaxonCode,
  isSubmittableRank,
}: {
  selection: TaxonomyListItem | undefined;
  taxonResponse: TaxonResponseSummary | undefined;
  rootTaxonCode: string;
  isSubmittableRank: (rankCode?: string) => boolean;
}): boolean =>
  !isRootTaxonResponse(taxonResponse, rootTaxonCode) &&
  isLeafSelection(selection, taxonResponse) &&
  !isSubmittableRank(taxonResponse?.rank.code);

export const buildTaxonomySections = (
  items: TaxonomyListItem[],
  iconColor: string,
  onSelect: (item: TaxonomyListItem) => void | Promise<void>,
): ListSectionProps[] => {
  const grouped = items.reduce<Record<string, TaxonomyListItem[]>>(
    (accumulator, item) => {
      const letter = (item.label.charAt(0) || '#').toUpperCase();
      if (!accumulator[letter]) {
        accumulator[letter] = [];
      }
      accumulator[letter].push(item);
      return accumulator;
    },
    {},
  );

  return Object.entries(grouped)
    .sort(([first], [second]) => first.localeCompare(second))
    .map(([letter, groupItems]) => ({
      id: letter,
      title: letter,
      items: groupItems.map((item) => ({
        id: item.id,
        title: item.label,
        subtitle: item.scientificDisplayName ?? '',
        iconAfter: (
          <Ionicons name="chevron-forward" size={18} color={iconColor} />
        ),
        onPress: (): void => {
          void onSelect(item);
        },
      })),
    }));
};

export const getLastAssignedTaxon = (
  selectedPath: TaxonomyListItem[],
): LastAssignedItemResult => {
  let unassignedOffset = 0;

  for (let index = selectedPath.length - 1; index >= 0; index -= 1) {
    const item = selectedPath[index];

    // If item is assigned, return it with offset
    if (!item.isUnassigned) {
      return {
        assignedTaxon: item,
        unassignedOffset,
      };
    }
    unassignedOffset++;
  }
  return { unassignedOffset: 0 };
};
